import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useBulkInsertModalityMutation,
  useBulkInsertTopicListMutation,
} from "../../../../../../api/services/bulkInsertActivity/bulkInsertApis";
import { useGetUsersDetailByRoleMutation } from "../../../../../../api/services/user/userApi";
import { MuiModal, OptionsDataType } from "../../../../../../components";
import { ActivityForm } from "../../../../../../components/activityForm/ActivityForm";
import { ROLE_IDS } from "../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import { getValueOrBlank, isNotEmpty } from "../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import {
  bulkActivityListSelector,
  bulkActivityProgramTemplateIdSelector,
} from "../../../../../../redux/slices/program/programSelector";
import {
  setBulkActivityList,
  updateActivityInBulkActivityList,
} from "../../../../../../redux/slices/program/programSlice";
import { convertFunctionsOrCoachToDropdown } from "../../../../../createProgram/helper";
import {
  AddActivityDefaultFormValue,
  AddActivityFormKey,
  AddActivityFormProps,
  AddActivityFormKey as bulkInsertActivityFormKey,
} from "../../../../../createProgram/modelsAndSchemas/Models";
import { AddActivitySchema } from "../../../../../createProgram/modelsAndSchemas/ValidationSchema";
import { activityTimelineSiteCoreKeys } from "../../../../../createProgram/siteCore/SiteCoreKeys";
import { CancelFieldModal } from "../../../../../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import {
  ActivityItemType,
  IdNameType,
} from "../../../../modelsAndSchemas/Models";
import { EditDeleteState } from "../BulkActivityDetails";
import { BulkInsertAddActivityWrapper } from "../BulkActivityDetails.style";

interface BulkActivityAddActivityFormProps {
  show: boolean;
  handleCloseModal: () => void;
  pageId: string;
  filteredActivityTypes: OptionsDataType[];
  editActivityData?: EditDeleteState;
  programIds: string[];
}

export const BulkActivityAddActivity: FC<BulkActivityAddActivityFormProps> = ({
  show,
  handleCloseModal,
  pageId,
  filteredActivityTypes,
  editActivityData,
  programIds,
}) => {
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();

  const dispatch = useDispatch();
  const { loggedInUser } = useGetLoggedInUserDetails();

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const [filteredModality, setFilteredModality] = useState<OptionsDataType[]>(
    [],
  );

  const programTemplateIds = useSelector(bulkActivityProgramTemplateIdSelector);

  const activityList = useSelector(bulkActivityListSelector);

  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);

  const topicRef = useRef<{ resetValues: (values?: string[]) => void }>(null);

  const [showCancel, setShowCancel] = useState<boolean>(false);
  const [isEffectExecuted, setIsEffectExecuted] = useState<boolean>(false);

  const getLabel = (key: string) => {
    return fetchLabel(activityTimelineSiteCoreKeys.activityTimeline, key);
  };

  const methods = useForm<AddActivityFormProps>({
    mode: "all",
    defaultValues: AddActivityDefaultFormValue,
    resolver: yupResolver(AddActivitySchema(commonValidationMessages)),
  });

  const {
    control,
    getValues,
    setValue,
    reset,
    formState: { isDirty },
    handleSubmit,
  } = methods;

  const {
    data: modalityListData,
    isPending: isModalityPending,
    isError: isModalityError,
    isSuccess: isModalitySuccess,
    mutateAsync: getModalityList,
    reset: clearListOfModalityData,
  } = useBulkInsertModalityMutation();

  const {
    mutateAsync: getTopicList,
    data: topicListData,
    isPending: isTopicPending,
    isError: isTopicError,
    isSuccess: isTopicSuccess,
    reset: clearListOfTopicData,
  } = useBulkInsertTopicListMutation();

  const {
    mutateAsync: getProgramCoaches,
    data: programCoachList,
    isPending: isCoachPending,
    isError: isCoachError,
  } = useGetUsersDetailByRoleMutation();

  useEffect(() => {
    getProgramCoaches({ roleIds: [ROLE_IDS.deliveryCoach] });
  }, []);

  useEffect(() => {
    if (editActivityData?.isEdit && !isEffectExecuted) {
      const editItem = activityList.find(
        (item) => item.id === editActivityData?.id,
      );

      getModalityApi(getValueOrBlank(editItem?.activityType));
      getTopicApi(
        getValueOrBlank(editItem?.activityType),
        getValueOrBlank(editItem?.modality),
      );

      const topicIds = editItem?.topic?.map((topic) => topic.id) ?? [];

      reset({
        [bulkInsertActivityFormKey.activityType]: getValueOrBlank(
          editItem?.activityType,
        ),
        [bulkInsertActivityFormKey.modality]: getValueOrBlank(
          editItem?.modality,
        ),
        [bulkInsertActivityFormKey.topic]: topicIds,
        [bulkInsertActivityFormKey.notes]: getValueOrBlank(editItem?.notes),

        [bulkInsertActivityFormKey.onBehalfOfCoachId]: getValueOrBlank(
          editItem?.onBehalfOfCoachId,
        ),
        [bulkInsertActivityFormKey.actualDurationInMinutes]: getValueOrBlank(
          editItem?.actualDurationInMinutes,
        ),
        [bulkInsertActivityFormKey.defaultDuration]: getValueOrBlank(
          editItem?.defaultDurationInMinutes,
        ),
        [bulkInsertActivityFormKey.activityDate]: getValueOrBlank(
          editItem?.activityDate,
        ),
      });

      if (isNotEmpty(topicListData)) {
        setIsEffectExecuted(true);
        topicRef.current?.resetValues(topicIds);
      }
    }
    if (!editActivityData?.isEdit) {
      // current date as default for activity date
      setValue(
        bulkInsertActivityFormKey.activityDate,
        dayjs().format(userCountryDateFormat),
      );
    }
  }, [editActivityData, isTopicSuccess, topicListData]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isModalityPending || isTopicPending || isCoachPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isModalityPending, isTopicPending, isCoachPending]);

  useEffect(() => {
    if (isModalityError || isTopicError || isCoachError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isModalityError, isTopicError, isCoachError]);

  useEffect(() => {
    if (isModalitySuccess && modalityListData) {
      // set modality list data to dropdown
      const dropdownValues = fetchDropdownValues(
        activityTimelineSiteCoreKeys.activityTimeline,
        activityTimelineSiteCoreKeys.modalityList,
      );

      const filtered = dropdownValues?.filter((item) =>
        modalityListData.some(
          (activity: IdNameType) => activity.name === item.value,
        ),
      );

      setFilteredModality(filtered);
    }
  }, [isModalitySuccess, modalityListData]);

  const closeMainModalHandler = () => {
    if (isDirty) setShowCancel(true);
    else handleCloseModal();
  };

  const activityTitle = () => {
    if (editActivityData?.isEdit) {
      return getLabel(activityTimelineSiteCoreKeys.editActivityHeader);
    }

    return getLabel(activityTimelineSiteCoreKeys.addActivityHeader);
  };

  const getHighestId = (): number => {
    if (activityList.length === 0) {
      return 1; // If no activities, return 1 as the default ID
    }

    const highestId = Math.max(...activityList.map((activity) => activity.id));
    return highestId + 1; // Return the next highest ID
  };

  const coachesDropdownOptions = useMemo(() => {
    if (programCoachList?.length) {
      return convertFunctionsOrCoachToDropdown(programCoachList);
    }
    return [];
  }, [programCoachList]);

  const getActivityTypeName = (activityTypeKey: string) => {
    return filteredActivityTypes.find((item) => item.value === activityTypeKey)
      ?.label;
  };

  const onSubmitClick = () => {
    const {
      activityType,
      modality,
      topic,
      notes,
      actualDurationInMinutes,
      onBehalfOfCoachId,
      activityDate,
    } = getValues();

    const formattedTopics = topic.map((topicId: string) => {
      const foundTopic = topicListData?.find(
        (item: { id: string; topic: string }) => item.id === topicId,
      );
      return foundTopic
        ? { id: foundTopic.id, topic: foundTopic.topic }
        : { id: topicId, topic: "Unknown Topic" };
    });

    const activityId = editActivityData?.isEdit
      ? editActivityData.id
      : getHighestId();

    const newActivity: ActivityItemType = {
      id: Number(activityId),
      activityType,
      activityTypeName: getValueOrBlank(getActivityTypeName(activityType)),
      modality,
      topic: formattedTopics,
      actualDurationInMinutes,
      defaultDurationInMinutes: getValueOrBlank(getValues().defaultDuration),
      onBehalfOfCoachId,
      onBehalfOfCoachName:
        coachesDropdownOptions?.find((item) => item.value === onBehalfOfCoachId)
          ?.label ?? "",
      notes,
      activityDate: activityDate,
      systemDate: dayjs().format(userCountryDateFormat),
    };

    // Check for duplicates in the activityList
    const isModalitySelected =
      !!newActivity.modality && newActivity.modality !== "";
    const isTopicSelected =
      Array.isArray(newActivity.topic) && newActivity.topic.length > 0;

    let duplicateActivity: ActivityItemType | undefined = undefined;

    if (isModalitySelected && isTopicSelected) {
      duplicateActivity = activityList
        .filter((item) => item.id !== newActivity.id)
        .find(
          (activity) =>
            activity.activityType === newActivity.activityType &&
            activity.modality === newActivity.modality &&
            activity.topic.map((t) => t.id).join(",") ===
              newActivity.topic.map((t) => t.id).join(",") &&
            activity.systemDate === dayjs().format(userCountryDateFormat),
        );
    }
    // as per PBI it should compare with system date, not activity date,
    // activity date was added later on and it is editable
    // so added one new key systemDate to compare with current date

    setIsDuplicate(!!duplicateActivity);

    if (!duplicateActivity) {
      if (editActivityData?.isEdit) {
        editActivity(newActivity); // Edit existing activity
      } else {
        addActivity(newActivity); // Add new activity
      }
    }
  };

  const addActivity = (newActivity: ActivityItemType) => {
    dispatch(setBulkActivityList([...activityList, newActivity]));
    dispatch(
      toggleToast({
        open: true,
        message: getLabel(
          activityTimelineSiteCoreKeys.activityAddedSuccessText,
        ),
      }),
    );
    handleCloseModal();
  };

  const editActivity = (updatedActivity: ActivityItemType) => {
    dispatch(updateActivityInBulkActivityList(updatedActivity));
    dispatch(
      toggleToast({
        open: true,
        message: getLabel(
          activityTimelineSiteCoreKeys.BulkActivityUpdatedToastMessage,
        ),
      }),
    );
    handleCloseModal();
  };

  const onConfirmClick = () => {
    handleSubmit(onSubmitClick)();
  };

  const closeModalHandler = () => setShowCancel(false);

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    dispatch(
      toggleToast({
        open: true,
        message: editActivityData?.isEdit
          ? getLabel(activityTimelineSiteCoreKeys.cancelConfirmationText)
          : getLabel(activityTimelineSiteCoreKeys.changesDiscardedText),
      }),
    );
    handleCloseModal();
  };

  const handleActivityTypeChange = (activityTypeKey: string) => {
    setIsEffectExecuted(true);
    resetModality();
    resetTopic();
    getModalityApi(activityTypeKey);
  };

  const getModalityApi = (activityTypeId: string) => {
    if (activityTypeId) {
      setTimeout(() => {
        getModalityList({
          programTemplateIds,
          activityTypeId,
          participants: programIds,
        });
      }, 0);
    }
  };

  const resetModality = () => {
    clearListOfModalityData();
    setFilteredModality([]);
    setValue(bulkInsertActivityFormKey.modality, "");
  };

  const resetTopic = () => {
    clearListOfTopicData();
    setValue(bulkInsertActivityFormKey.topic, []);
    topicRef.current?.resetValues([]);
  };

  const handleModalityChange = (modalityKey: string) => {
    setIsEffectExecuted(true);
    resetTopic();
    const activityTypeKey = getValues(bulkInsertActivityFormKey.activityType);
    if (activityTypeKey) {
      getTopicApi(activityTypeKey, modalityKey);
    }
  };

  const getTopicApi = (activityTypeKey: string, modalityKey: string) => {
    getTopicList({
      programTemplateIds,
      activityTypeKey,
      modalityKey,
      participants: programIds,
    });
  };

  const topic = useWatch({
    name: AddActivityFormKey.topic,
    control,
    defaultValue: [],
  });

  useEffect(() => {
    setIsDuplicate(false);
  }, [topic]);

  return (
    <MuiModal
      open={show}
      onClose={closeMainModalHandler}
      title={activityTitle()}
      primaryButtonTitle={
        editActivityData?.isEdit ? commonLabels.update : commonLabels.save
      }
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={onConfirmClick}
      onSecondaryButtonClick={closeMainModalHandler}
      hasLargeContent
      footerErrorMessage={
        isDuplicate
          ? getLabel(activityTimelineSiteCoreKeys.duplicateActivityErrorMessage)
          : ""
      }
    >
      <BulkInsertAddActivityWrapper data-testid={`${pageId}-bulk-activity-add`}>
        <FormProvider {...methods}>
          <ActivityForm
            pageId={pageId}
            filteredActivityTypes={filteredActivityTypes}
            filteredModality={filteredModality}
            topicListData={topicListData}
            programCoachList={programCoachList ?? []}
            topicRef={topicRef}
            handleActivityTypeChange={handleActivityTypeChange}
            handleModalityChange={handleModalityChange}
          />
        </FormProvider>

        {showCancel && (
          <CancelFieldModal
            showModal={showCancel}
            handleModalClose={closeModalHandler}
            handlePrimaryButtonClick={cancelConfirmHandler}
            cancelMessage={getLabel(
              activityTimelineSiteCoreKeys.bulkActivityCancelConfirmationText,
            )}
          />
        )}
      </BulkInsertAddActivityWrapper>
    </MuiModal>
  );
};
